import { Colors } from 'context';
import { CareerSkillStatus } from 'core/hooks/CareerSkillGraph/transform/transformCareerSkillGraph';
import { CareerIconName } from 'atoms/CareerIcon/CareerIcon';

/**
 * Как выглядит статус узла (ТЗ 0070).
 *
 * 🔴 ЧЕТЫРЕ канала на каждый статус: цвет, иконка, слово и стиль рамки. ТЗ
 * требует «статус не должен передаваться только цветом», и это не про
 * дальтонизм в общем виде: карта — плотный экран, где семь состояний семи
 * оттенками не различает никто, включая людей с обычным зрением.
 *
 * 🔴 Жёлтого (`main`) здесь нет ни у одного статуса. Он цвет ЗАЛИВКИ кнопки, а
 * не смысловая пометка: контраст #FFDF17 на белом примерно 1.4 : 1 при норме
 * 4.5 : 1. Правило записано в шапке `colors.ts` и закреплено гейтом
 * `themeContrast.test.ts`.
 */
/**
 * Заливка узла по статусу — ЧЕТВЁРТЫЙ канал после цвета рамки, её штриха и
 * слова. Она светлая настолько, чтобы тёмный текст поверх оставался читаемым:
 * цвет здесь помогает найти узел взглядом, а называет статус по-прежнему
 * слово.
 *
 * 🔴 Значения едут через CSS-переменную, а НЕ литералом в `fill`. Тема здесь
 * настоящая: `black` = #222 в светлой и #ECECEC в тёмной. Литеральный
 * #ECFDF5 означал бы почти белую карточку со светло-серым текстом в тёмной
 * теме — «сборка зелёная, тесты зелёные, а на экране пусто», ровно случай из
 * шапки `colors.ts`. Переменные объявляет холст (`SkillGraphCanvas`), значения
 * лежат ниже парами, и добавить статус мимо обеих тем нельзя: обе карты
 * типизированы одним `Record`, и это проверяет `skillStatus.test.ts`.
 *
 * Почему литеральные цвета, а не токены палитры: это ось СМЫСЛА («закрыто /
 * горит / можно потом»), а не акцент интерфейса. Токенов такой светлоты в
 * палитре нет вовсе, а `bg_gray` на все семь статусов значит вернуться к
 * «различайте рамкой».
 */
export const STATUS_TINT: Record<CareerSkillStatus, string> = {
  confirmed: 'var(--skm-tint-confirmed)',
  declared: 'var(--skm-tint-declared)',
  in_progress: 'var(--skm-tint-in-progress)',
  missing_critical: 'var(--skm-tint-missing-critical)',
  missing: 'var(--skm-tint-missing)',
  optional: 'var(--skm-tint-optional)',
  hidden: 'var(--skm-tint-hidden)',
};

/** Светлая тема: текст #222 даёт на этих заливках 14.5:1, подпись — 4.8:1. */
export const STATUS_TINT_LIGHT: Record<CareerSkillStatus, string> = {
  confirmed: '#ECFDF5',
  declared: '#F0FDF4',
  in_progress: '#EFF6FF',
  missing_critical: '#FEF2F2',
  missing: '#F8FAFC',
  optional: '#FAFAF9',
  hidden: '#F5F5F4',
};

/** Тёмная тема: те же семь тонов, перевёрнутые. Текст #ECECEC — от 13:1. */
export const STATUS_TINT_DARK: Record<CareerSkillStatus, string> = {
  confirmed: '#0C2A22',
  declared: '#11291D',
  in_progress: '#10233B',
  missing_critical: '#331617',
  missing: '#1F2328',
  optional: '#232120',
  hidden: '#1C1C1B',
};

/** Имя переменной статуса: `missing_critical` → `--skm-tint-missing-critical`. */
export const tintVarName = (status: string): string => `--skm-tint-${status.replace(/_/g, '-')}`;

/**
 * Объявления переменных для вставки в стили холста. Печатаются из тех же
 * карт, что и `STATUS_TINT`, — руками их не переписывают, иначе тема и
 * заливка разъедутся молча.
 */
export const tintVars = (map: Record<CareerSkillStatus, string>): string =>
  Object.entries(map)
    .map(([status, color]) => `${tintVarName(status)}: ${color};`)
    .join('\n');

export interface ISkillStatusLook {
  /** Цвет рамки и иконки. Текст узла всегда обычный. */
  accent: string;
  /** Стиль рамки — второй канал после цвета. */
  border: 'solid' | 'dashed' | 'double' | 'dotted';
  icon: CareerIconName;
  /** Ключ подписи. Слово — третий канал, и оно обязательное. */
  labelKey: string;
  /** Насыщенность узла: закрытое спокойно, недостающее заметно. */
  muted: boolean;
}

export const SKILL_STATUS_LOOK: Record<CareerSkillStatus, ISkillStatusLook> = {
  confirmed: {
    accent: Colors.signal_green,
    border: 'solid',
    icon: 'check',
    labelKey: 'careerCenter.map.status.confirmed',
    muted: true,
  },
  declared: {
    accent: Colors.marker_green,
    border: 'dashed',
    icon: 'check',
    labelKey: 'careerCenter.map.status.declared',
    muted: true,
  },
  in_progress: {
    accent: Colors.gray_dark,
    border: 'double',
    icon: 'clock',
    labelKey: 'careerCenter.map.status.in_progress',
    muted: false,
  },
  missing_critical: {
    accent: Colors.signal_red,
    border: 'solid',
    icon: 'warning',
    labelKey: 'careerCenter.map.status.missing_critical',
    muted: false,
  },
  missing: {
    accent: Colors.gray_dark,
    border: 'solid',
    icon: 'goal',
    labelKey: 'careerCenter.map.status.missing',
    muted: false,
  },
  optional: {
    // 🔴 `gray_dark`, а не `gray`: акцент красит и ТЕКСТ подписи статуса, а
    // бледно-серый как текст не проходит контраст. Гейт themeContrast видит
    // только прямые `color:` в стилях и эту косвенность не ловит — значит
    // правило приходится помнить здесь.
    accent: Colors.gray_dark,
    border: 'dotted',
    icon: 'star',
    labelKey: 'careerCenter.map.status.optional',
    muted: true,
  },
  hidden: {
    accent: Colors.gray_dark,
    border: 'dashed',
    icon: 'eye',
    labelKey: 'careerCenter.map.status.hidden',
    muted: true,
  },
};

/** Подпись вида связи. Легенда называет их словами, а не только штрихом. */
export const EDGE_LABEL_KEY: Record<string, string> = {
  required: 'careerCenter.map.edge.required',
  recommended: 'careerCenter.map.edge.recommended',
};
